import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home as HomeIcon, Shirt, Smartphone, Sparkles, Flower2, Dumbbell, Baby, Car, Star, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { fetchCategories, fetchHome } from '../lib/api';
import { heroSlides, universes } from '../lib/constants';

const iconMap = { Home: HomeIcon, Shirt, Smartphone, Sparkles, Flower2, Dumbbell, Baby, Car };

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [categories, setCategories] = useState([]);
  const [deals, setDeals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
    fetchHome().then((d) => { setDeals(d.deals); setBestSellers(d.bestSellers); }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const current = heroSlides[slide];

  return (
    <div className="bg-white" data-testid="home-page">
      {/* Hero carousel */}
      <section className={`relative overflow-hidden bg-gradient-to-r ${current.bg}`}>
        <div className="max-w-7xl mx-auto px-4 py-10 md:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div key={slide} className="animate-fade-in-up">
            <div className="inline-block bg-[#FF4D4D] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">OFFRE LIMITÉE</div>
            <h1 className="text-4xl md:text-6xl font-black text-[#262626] leading-tight">{current.title}</h1>
            <p className="text-lg text-[#595959] mt-4">{current.subtitle}</p>
            <div className="mt-6 flex items-center gap-3">
              <Link to={current.link}>
                <Button data-testid="hero-cta" className="bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-12 px-8 text-base font-semibold">
                  {current.cta}
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-sm text-[#595959]">
                <Clock size={16} /> Fin dans 2j 14h
              </div>
            </div>
          </div>
          <div className="relative">
            <img src={current.image} alt={current.title} className="w-full h-[280px] md:h-[400px] object-cover rounded-2xl shadow-xl" />
          </div>
        </div>

        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex gap-2">
          <button onClick={() => setSlide((slide - 1 + heroSlides.length) % heroSlides.length)} className="w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow">
            <ChevronLeft />
          </button>
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <button onClick={() => setSlide((slide + 1) % heroSlides.length)} className="w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow">
            <ChevronRight />
          </button>
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-2 rounded-full ${i === slide ? 'w-8 bg-[#00847A]' : 'w-2 bg-neutral-400'}`} />
          ))}
        </div>
      </section>

      {/* Universes */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <h2 className="text-2xl md:text-3xl font-black text-[#262626] mb-6">Explorez nos univers</h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {universes.map((u, i) => {
            const Icon = iconMap[u.icon] || HomeIcon;
            return (
              <Link key={i} data-testid={`universe-${u.categoryId}`} to={`/categorie/${u.categoryId}`} className={`${u.color} rounded-xl p-5 flex flex-col items-center text-center hover:shadow-lg hover:-translate-y-1`} style={{ transition: 'transform 200ms ease, box-shadow 200ms ease' }}>
                <div className={`w-14 h-14 rounded-full bg-white ${u.textColor} flex items-center justify-center mb-3`}>
                  <Icon size={28} />
                </div>
                <span className="text-sm font-semibold text-[#262626]">{u.name}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Flash Deals */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl md:text-3xl font-black text-[#262626]">
              <span className="text-[#FF4D4D]">Offres du moment</span> · jusqu'à -50%
            </h2>
          </div>
          <Link to="/categorie/tous" className="text-sm font-semibold text-[#00847A] hover:underline">Voir tout →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="deals-grid">
          {deals.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Categories grid */}
      <section className="bg-neutral-50 py-10">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-[#262626] mb-6">Toutes nos catégories</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link key={cat.id} data-testid={`category-tile-${cat.id}`} to={`/categorie/${cat.id}`} className="group bg-white rounded-xl overflow-hidden border border-neutral-200 hover:border-[#00847A] hover:shadow-lg">
                <div className="aspect-square overflow-hidden">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110" style={{ transition: 'transform 400ms ease' }} loading="lazy" />
                </div>
                <div className="p-3 text-center">
                  <h3 className="font-semibold text-sm text-[#262626] group-hover:text-[#00847A]">{cat.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Best sellers */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-black text-[#262626]">Nos meilleures ventes</h2>
          <Link to="/categorie/tous" className="text-sm font-semibold text-[#00847A] hover:underline">Voir tout →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="bestsellers-grid">
          {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Reviews/Trust */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="bg-gradient-to-br from-[#00847A] to-[#006B63] rounded-2xl p-8 md:p-12 text-white grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={24} className="fill-[#FFB800] text-[#FFB800]" />)}
            </div>
            <div className="text-4xl font-black">4,6/5</div>
            <div className="text-white/80 text-sm mt-1">Sur plus de 500 000 avis</div>
          </div>
          <div className="md:col-span-2 grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className="fill-[#FFB800] text-[#FFB800]" />)}
              </div>
              <p className="text-sm text-white/90">«Très belle plateforme, livraison rapide et produits conformes à la description.»</p>
              <div className="text-xs text-white/70 mt-2">— Thomas B., Lyon</div>
            </div>
            <div>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className="fill-[#FFB800] text-[#FFB800]" />)}
              </div>
              <p className="text-sm text-white/90">«Grand choix de marques, prix très compétitifs. Je recommande sans hésiter !»</p>
              <div className="text-xs text-white/70 mt-2">— Catherine M., Paris</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
